import type { PendingMedia } from "./GiftMediaPreview";

// Shown only for "Unlock later" (per the brief's own framing - View Once/
// Keep keep sending directly, matching today's behavior). Summary only -
// no eager preview beyond what the sender already saw composing it, no
// credit cost line (Custom Gifts of every mode are free - confirmed
// server-side, POST /api/gifts never touches creditsDB - so this
// deliberately does NOT show the reference mockup's "💎 20", it says
// "Private Drop" instead, per the product owner's own explicit note).
export function PrivateDropReview({
  media,
  recipientName,
  unlockSummary,
  message,
  sending,
  openTogether,
  onToggleOpenTogether,
  onEdit,
  onSend,
}: {
  media: PendingMedia;
  recipientName: string;
  unlockSummary: string;
  message: string;
  sending: boolean;
  // "Open it together" (V2 Part B) - independent of delivery mode, only
  // offered here (unlock-later's own review step) since it's meaningless
  // for View Once/Keep, which never show this screen at all.
  openTogether: boolean;
  onToggleOpenTogether: (value: boolean) => void;
  onEdit: () => void;
  onSend: () => void;
}) {
  const mediaLabel = media.kind === "voice" ? "Voice message" : media.kind === "video" ? "Video" : "Photo";
  const mediaIcon = media.kind === "voice" ? "bi-mic-fill" : media.kind === "video" ? "bi-camera-video-fill" : "bi-image-fill";

  return (
    <div className="private-drop-review">
      <span className="send-gift-section-label">Review your drop</span>
      <div className="private-drop-review-card">
        <div className="private-drop-review-media-row">
          <span className="private-drop-review-media-icon">
            <i className={`bi ${mediaIcon}`} aria-hidden="true" />
          </span>
          <div>
            <div className="private-drop-review-media-title">Private Drop</div>
            <div className="private-drop-review-media-subtitle">{mediaLabel}</div>
          </div>
        </div>

        <div className="private-drop-review-divider" />

        <div className="private-drop-review-row">
          <span className="private-drop-review-row-label">To</span>
          <span className="private-drop-review-row-value">{recipientName}</span>
        </div>

        <div className="private-drop-review-row">
          <span className="private-drop-review-row-label">
            <i className="bi bi-lock-fill" aria-hidden="true" /> Unlock later
          </span>
        </div>

        <div className="private-drop-review-row">
          <span className="private-drop-review-row-label">
            <i className="bi bi-calendar-event" aria-hidden="true" /> Unlocks on
          </span>
          <span className="private-drop-review-row-value">{unlockSummary}</span>
        </div>

        {message && (
          <div className="private-drop-review-row">
            <span className="private-drop-review-row-label">
              <i className="bi bi-envelope-heart" aria-hidden="true" /> Message
            </span>
            <span className="private-drop-review-row-value">&ldquo;{message}&rdquo;</span>
          </div>
        )}
      </div>

      <button
        type="button"
        className={`private-drop-open-together-toggle${openTogether ? " is-active" : ""}`}
        role="switch"
        aria-checked={openTogether}
        aria-label="Open Together"
        onClick={() => onToggleOpenTogether(!openTogether)}
        disabled={sending}
      >
        <span className="private-drop-open-together-icon" aria-hidden="true">
          <i className="bi bi-people-fill" />
        </span>
        <span className="private-drop-open-together-copy">
          <span className="private-drop-open-together-title">Open it together</span>
          <span className="private-drop-open-together-subtitle">You'll both wait for the other before it reveals.</span>
        </span>
        <span className="private-drop-open-together-switch" aria-hidden="true" />
      </button>

      <div className="private-drop-review-actions">
        <button type="button" className="btn btn-outline-pink btn-sm" onClick={onEdit} disabled={sending}>
          Edit
        </button>
        <button type="button" className="send-gift-submit" onClick={onSend} disabled={sending}>
          <i className="bi bi-gift-fill" aria-hidden="true" />
          {sending ? "Sending…" : "Send Private Drop"}
        </button>
      </div>
    </div>
  );
}
